import type { Product, ProductVariant } from '../types';

/**
 * Coerce `variant.images` (array, JSON string, URLs, nested objects)
 * into a flat list of image URL strings — used across PDP, cards, mapper output.
 */
export function normalizeVariantImages(
  variant: ProductVariant | null | undefined,
): string[] {
  if (!variant?.images) return [];

  let raw: unknown = variant.images;
  if (typeof raw === 'string') {
    const trimmed = raw.trim();
    if (trimmed.startsWith('[')) {
      try {
        raw = JSON.parse(trimmed);
      } catch {
        return trimmed ? [trimmed] : [];
      }
    } else if (trimmed) {
      return [trimmed];
    } else {
      return [];
    }
  }

  const list = Array.isArray(raw) ? raw : [raw];
  return list
    .map((item: unknown) => {
      if (typeof item === 'string' && item) return item;
      if (item && typeof item === 'object') {
        const o = item as Record<string, unknown>;
        const u = o.image ?? o.url ?? o.src;
        return typeof u === 'string' && u ? u : null;
      }
      return null;
    })
    .filter((x): x is string => Boolean(x));
}

export function productHasVariants(product: Product): boolean {
  return Array.isArray(product.attributes) && product.attributes.length > 0;
}

/** Catalog / PDP / cards: variants ⇒ first variant’s gallery only — never unrelated main `imageUrl` */
export function getProductDisplayImageUrls(product: Product): string[] {
  if (productHasVariants(product)) {
    return normalizeVariantImages(product.attributes![0]);
  }
  const u = product.imageUrl;
  if (!u) return [];
  return typeof u === 'string' ? [u] : [...u];
}

export function getProductDisplayPrimaryImageUrl(
  product: Product,
): string | undefined {
  const urls = getProductDisplayImageUrls(product);
  return urls[0];
}

/** Cart / wishlist snapshot: chosen variant wins; multi-variant SKUs skip main hero */
export function resolveLineItemImageUrl(
  product: Product,
  selectedVariant: ProductVariant | null,
): string | undefined {
  const fromSel = normalizeVariantImages(selectedVariant ?? undefined);
  if (fromSel.length > 0) return fromSel[0];

  if (productHasVariants(product)) {
    const list = normalizeVariantImages(product.attributes![0]);
    if (list.length > 0) return list[0];
    return undefined;
  }

  const main = product.imageUrl;
  if (!main) return undefined;
  return Array.isArray(main) ? main[0] : main;
}

/** Attribute modal: prefer selected variant imgs, else variant-derived fallbacks only */
export function getModalVariantGalleryUrls(
  product: Product,
  selectedVariant: ProductVariant | null,
): string[] {
  const sel = normalizeVariantImages(selectedVariant ?? undefined);
  if (sel.length > 0) return sel;

  if (productHasVariants(product)) {
    const first = normalizeVariantImages(product.attributes![0]);
    if (first.length > 0) return first;

    for (const v of product.attributes!) {
      const u = normalizeVariantImages(v);
      if (u.length > 0) return u;
    }
    return [];
  }

  const u = product.imageUrl;
  if (!u) return [];
  return typeof u === 'string' ? [u] : [...u];
}
